import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { aiDocumentIntakeApi } from 'app/services/api';
import AiSparkleIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/AiSparkleIcon';
import MultiFileDropzone from 'app/shared/modules/LoanApplicationDocuments/MultiFileDropzone';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { DocumentIntakeJobStatus } from 'constants/documentIntake';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getDefaultErrorMessage } from 'utils/axios';
import { logger } from 'utils/logger';

import {
  clearActiveJobForLoanAction,
  createIntakeJobAction,
  startIntakePollAction,
} from 'store/documentIntake';
import { showErrorMessageAction, showMessageAction } from 'store/global/actions';
import { getDocumentTypesAction } from 'store/system/actions';
import { getDocumentTypes } from 'store/system/selectors';

import {
  ACCEPTED_FILE_TYPES,
  BatchIntakeStep,
  INTAKE_MODE_TILES,
  ReviewFilter,
  SPLIT_MAX_FILE_SIZE_BYTES,
  SPLIT_MAX_FILE_SIZE_MB,
} from './AiBatchIntake.constants';
import {
  confirmButtonIconStyle,
  drawerContentStyles,
  getReviewFilterSegmentItemStyles,
  modeTileContentStyles,
  modeTileIconStyles,
  modeTilesRowStyles,
  modeTileStyles,
  reviewCompletionBannerStyles,
  reviewConfirmButtonStyles,
  reviewFilterSegmentStyles,
  reviewFooterActionsStyles,
  reviewFooterStyles,
  reviewFooterTallyStyles,
  reviewHeaderActionsStyles,
  reviewHeaderAiBadgeStyles,
  reviewHeaderInfoStyles,
  reviewHeaderStyles,
  reviewNeedsReviewInlineStyles,
  reviewScrollAreaStyles,
  reviewToolbarStyles,
  splitDividerChipStyles,
  splitDividerLineStyles,
  splitDividerStyles,
} from './AiBatchIntake.styles';
import { AiBatchIntakeTestIds } from './AiBatchIntake.testIds';
import {
  buildSegmentsFromResultJson,
  deriveIntakeCompletionText,
  deriveSubtitleText,
  getEffectivePageIndices,
  isSegmentLowConfidence,
  isSegmentUnrecognized,
  mergeSegmentWithNext,
  mergeSegmentWithPrev,
  removeSegment,
  restoreSegment,
  splitSegmentBefore,
} from './AiBatchIntake.utils';
import IntakeSegmentRow from './IntakeSegmentRow';

/**
 * AI Batch Intake — drop one combined PDF; the backend splits and classifies it
 * in the background. The modal closes immediately (fire-and-forget). When the
 * job finishes the user returns via AI Activity to review and file the segments.
 *
 * Steps: UPLOAD → (background processing) → REVIEW & FILE
 *
 * Review-on-return hydrates from job.resultJson via the `job` prop.
 *
 * DEV-4555: adds per-segment page preview (thumbnail rail + lightbox),
 * boundary editing (split-here / merge-with-next / merge-with-prev / remove+restore),
 * re-classify after edits, and updated fileSegments with page-range overrides.
 * The footer tally now shows "N of M will be filed · K removed · J edited".
 *
 * Backend route:
 *   Job: POST document-intake-jobs  (Lender.Optimus.Ai DEV-4424)
 */

const AiBatchIntake = ({ onComplete, job }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const documentTypes = useSelector(getDocumentTypes, _.isEqual);

  const isBackgroundReview = !!job;
  const initialStep = isBackgroundReview ? BatchIntakeStep.REVIEW : BatchIntakeStep.UPLOAD;

  const [step, setStep] = useState(initialStep);
  const [isSinglePdf, setIsSinglePdf] = useState(!isBackgroundReview);
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [segments, setSegments] = useState(() =>
    isBackgroundReview && job?.resultJson
      ? buildSegmentsFromResultJson(job.resultJson, job.id)
      : [],
  );
  const [isFiling, setIsFiling] = useState(false);
  const [reviewFilter, setReviewFilter] = useState(ReviewFilter.ALL);
  const [splitPickerSegId, setSplitPickerSegId] = useState(null);
  const [splitPickerPage, setSplitPickerPage] = useState(null);
  const [reclassifyingSegIds, setReclassifyingSegIds] = useState(new Set());
  const [previewOpenSegId, setPreviewOpenSegId] = useState(null);

  const stopPollRef = useRef(null);

  const documentTypeOptions = useMemo(
    () =>
      Object.values(documentTypes ?? {}).map(dt => ({
        value: dt.documentTypeId ?? dt.id,
        name: dt.documentTypeName ?? dt.name,
      })),
    [documentTypes],
  );

  const processFiles = useCallback(
    async files => {
      if (!files?.length) {
        return;
      }

      const isSingle = files.length === 1 && files[0].type === 'application/pdf';

      setIsSinglePdf(isSingle);

      const oversized = files.find(f => f.size > SPLIT_MAX_FILE_SIZE_BYTES);

      if (oversized) {
        dispatch(
          showErrorMessageAction({
            message: `File "${oversized.name}" is too large. Maximum size per file is ${SPLIT_MAX_FILE_SIZE_MB} MB.`,
          }),
        );

        return;
      }

      setSelectedFileNames(files.map(f => f.name));

      const uploadLabel = files.length === 1 ? files[0].name : `${files.length} files`;

      try {
        const createdJob = await dispatch(
          createIntakeJobAction({
            files,
            loanApplicationId: applicationId,
            uploadLabel,
          }),
        );

        dispatch(
          showMessageAction({
            message:
              "Document intake started — working in the background. You'll see progress here in Documents.",
            variant: 'info',
          }),
        );

        const stop = dispatch(startIntakePollAction({ id: createdJob.id }));

        stopPollRef.current = stop;

        onComplete?.();
      } catch (err) {
        logger.error(err);
        dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
      }
    },
    [applicationId, dispatch, onComplete],
  );

  const onSegmentTypeChange = useCallback(
    segId => (event, value) => {
      setSegments(prev =>
        prev.map(s => (s.id === segId ? { ...s, documentTypeId: value?.value ?? null } : s)),
      );
    },
    [],
  );

  const onToggleSegment = useCallback(
    segId => () => {
      setSegments(prev =>
        prev.map(s => (!s.isRemoved && s.id === segId ? { ...s, isSelected: !s.isSelected } : s)),
      );
    },
    [],
  );

  // ── Merge ────────────────────────────────────────────────────────────────────

  const onMergeWithNext = useCallback(
    segId => () => {
      setSegments(prev => {
        const idx = prev.findIndex(s => s.id === segId);

        return mergeSegmentWithNext(prev, idx);
      });
    },
    [],
  );

  const onMergeWithPrev = useCallback(
    segId => () => {
      setSegments(prev => {
        const idx = prev.findIndex(s => s.id === segId);

        return mergeSegmentWithPrev(prev, idx);
      });
    },
    [],
  );

  // ── Split-here picker ─────────────────────────────────────────────────────────

  const onOpenSplitPicker = useCallback(
    segId => () => {
      setSplitPickerSegId(prev => (prev === segId ? null : segId));
      setSplitPickerPage(null);
    },
    [],
  );

  const onSplitPageSelect = useCallback(pageNum => () => setSplitPickerPage(pageNum), []);

  const onApplySplit = useCallback(() => {
    if (splitPickerSegId == null || splitPickerPage == null) {
      return;
    }

    setSegments(prev => {
      const idx = prev.findIndex(s => s.id === splitPickerSegId);

      return splitSegmentBefore(prev, idx, splitPickerPage);
    });

    setSplitPickerSegId(null);
    setSplitPickerPage(null);
  }, [splitPickerSegId, splitPickerPage]);

  const onCancelSplit = useCallback(() => {
    setSplitPickerSegId(null);
    setSplitPickerPage(null);
  }, []);

  // ── Remove / Restore ──────────────────────────────────────────────────────────

  const onRemoveSegment = useCallback(
    segId => () => {
      setSegments(prev => removeSegment(prev, segId));
    },
    [],
  );

  const onRestoreSegment = useCallback(
    segId => () => {
      setSegments(prev => restoreSegment(prev, segId));
    },
    [],
  );

  // ── Preview toggle ────────────────────────────────────────────────────────────

  const onTogglePreview = useCallback(
    segId => () => {
      setPreviewOpenSegId(prev => (prev === segId ? null : segId));
    },
    [],
  );

  // ── Load source content (passed down to IntakeSegmentPreview) ─────────────────

  const onLoadSource = useCallback(
    ({ jobId, sourceIndex }) => aiDocumentIntakeApi.getSourceContent({ jobId, sourceIndex }),
    [],
  );

  // ── Re-classify ───────────────────────────────────────────────────────────────

  const onReclassify = useCallback(
    seg => async () => {
      if (!seg.jobId) {
        return;
      }

      setReclassifyingSegIds(prev => new Set([...prev, seg.id]));

      try {
        const { startPageIndex, endPageIndex } = getEffectivePageIndices(seg);
        const result = await aiDocumentIntakeApi.reclassifySegment({
          jobId: seg.jobId,
          sourceIndex: seg.sourceIndex,
          startPageIndex,
          endPageIndex,
        });

        setSegments(prev =>
          prev.map(s =>
            s.id === seg.id
              ? {
                  ...s,
                  documentTypeId: result.documentTypeId ?? s.documentTypeId,
                  classifyConfidence: result.confidence ?? null,
                }
              : s,
          ),
        );
      } catch (err) {
        logger.error('[AiBatchIntake] reclassify failed', err);
        dispatch(showErrorMessageAction({ message: 'Re-classify failed — please try again.' }));
      } finally {
        setReclassifyingSegIds(prev => {
          const next = new Set(prev);

          next.delete(seg.id);

          return next;
        });
      }
    },
    [dispatch],
  );

  // ── Legacy merge-up (non-background review path) ──────────────────────────────

  const onMergeUp = useCallback(
    segId => () => {
      setSegments(prev => {
        const idx = prev.findIndex(s => s.id === segId);

        if (idx <= 0) {
          return prev;
        }

        const prev0 = prev[idx - 1];
        const curr = prev[idx];
        const merged = {
          ...prev0,
          pageEnd: curr.pageEnd ?? prev0.pageEnd,
          classifyConfidence: null,
          documentTypeId: prev0.documentTypeId ?? curr.documentTypeId,
        };

        return [...prev.slice(0, idx - 1), merged, ...prev.slice(idx + 1)];
      });
    },
    [],
  );

  const onFilterChange = useCallback(filter => () => setReviewFilter(filter), []);

  const onCancelToUpload = useCallback(() => setStep(BatchIntakeStep.UPLOAD), []);

  const onStopPropagation = useCallback(e => e.stopPropagation(), []);

  const onConfirmAndFile = useCallback(async () => {
    if (!applicationId) {
      return;
    }

    const toFile = segments.filter(s => s.isSelected && s.documentTypeId && !s.isRemoved);

    if (!toFile.length) {
      dispatch(
        showErrorMessageAction({
          message: 'Select at least one document with a type assigned before filing.',
        }),
      );

      return;
    }

    setIsFiling(true);

    try {
      const segmentPayloads = toFile.map(seg => {
        const base = {
          segmentIndex: seg.segmentIndex,
          documentTypeId: seg.documentTypeId,
          loanApplicationId: applicationId,
        };

        if (!seg.isEdited) {
          return base;
        }

        const { startPageIndex, endPageIndex } = getEffectivePageIndices(seg);

        return { ...base, startPageIndex, endPageIndex, sourceIndex: seg.sourceIndex };
      });

      const bgApiResult = await aiDocumentIntakeApi
        .fileSegments({
          id: toFile[0].jobId,
          segments: segmentPayloads,
        })
        .catch(() => null);

      // DEV-4555: fileSegments returns FiledIntakeSegmentResult per segment
      // { segmentIndex, succeeded, failureReason, documentId }.
      const results = Array.isArray(bgApiResult) ? bgApiResult : [];
      const filedSegmentIndexSet = new Set(
        results.filter(r => r.succeeded !== false).map(r => r.segmentIndex),
      );
      const failedResults = results.filter(r => r.succeeded === false);
      const filedCount = filedSegmentIndexSet.size;
      const failedCount = bgApiResult === null ? toFile.length : toFile.length - filedCount;

      if (filedCount > 0) {
        dispatch(
          showMessageAction({
            message: `${filedCount} document${filedCount === 1 ? '' : 's'} filed successfully.${
              failedCount > 0 ? ` ${failedCount} could not be filed — try again.` : ''
            }`,
            variant: 'success',
          }),
        );

        failedResults.forEach(r => {
          if (r.failureReason) {
            dispatch(
              showErrorMessageAction({
                message: `Segment ${r.segmentIndex + 1} failed: ${r.failureReason}`,
              }),
            );
          }
        });
      }

      if (failedCount > 0 && filedCount === 0) {
        dispatch(
          showErrorMessageAction({
            message: `Failed to file ${failedCount} document${failedCount === 1 ? '' : 's'}.`,
          }),
        );
      }

      const filedBgIds = new Set(
        toFile.filter(seg => filedSegmentIndexSet.has(seg.segmentIndex)).map(seg => seg.id),
      );
      const remainingSegments = segments.filter(s => !filedBgIds.has(s.id));

      if (filedCount > 0) {
        setSegments(remainingSegments);
      }

      // DEV-4930: keep the review open + the job active while unrecognized (untyped)
      // segments remain — the user can assign a type and file them via this same path.
      // Only tear down once nothing unrecognized is left: a full success then also
      // clears the job, and any fully-resolved outcome closes the drawer. The job is
      // persisted server-side and resumable from the intake status surface, so this
      // never strands the plan.
      const hasUnrecognizedRemaining = remainingSegments.some(
        s => !s.isRemoved && isSegmentUnrecognized(s),
      );

      if (!hasUnrecognizedRemaining) {
        if (filedCount > 0 && failedCount === 0) {
          dispatch(clearActiveJobForLoanAction({ loanApplicationId: applicationId }));
        }

        onComplete?.();
      }
    } catch (err) {
      logger.error(err);
      dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err }));
    } finally {
      setIsFiling(false);
    }
  }, [applicationId, dispatch, onComplete, segments]);

  useEffect(
    () => () => {
      stopPollRef.current?.();
    },
    [],
  );

  useEffectSafeStrictMode(() => {
    if (!documentTypes || Object.keys(documentTypes).length === 0) {
      dispatch(getDocumentTypesAction());
    }
    // Only run at mount — intentional snapshot of documentTypes at open time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived counts ────────────────────────────────────────────────────────────

  const activeSegments = segments.filter(s => !s.isRemoved);
  const removedCount = segments.filter(s => s.isRemoved).length;
  const editedCount = segments.filter(s => s.isEdited && !s.isRemoved).length;
  const selectedCount = activeSegments.filter(s => s.isSelected).length;
  const needsReviewCount = activeSegments.filter(
    s => s.isSelected && isSegmentLowConfidence(s.classifyConfidence),
  ).length;
  const unassignedCount = activeSegments.filter(s => s.isSelected && !s.documentTypeId).length;
  const readyCount = activeSegments.filter(
    s => s.isSelected && s.documentTypeId && !isSegmentLowConfidence(s.classifyConfidence),
  ).length;
  const filableCount = activeSegments.filter(s => s.isSelected && s.documentTypeId).length;

  // Live count of truly-unrecognized segments (untyped, not Failed) — matches the
  // backend UnrecognizedSegments definition and shrinks as the user assigns types /
  // files them, so the completion banner drops toward 0.
  const unrecognizedRemaining = activeSegments.filter(isSegmentUnrecognized).length;
  const completionText = isBackgroundReview
    ? deriveIntakeCompletionText({
        unrecognizedSegments: unrecognizedRemaining,
        failedSegments: job?.failedSegments ?? null,
      })
    : null;

  const displayedSegments =
    reviewFilter === ReviewFilter.NEEDS_REVIEW
      ? segments.filter(s => !s.isRemoved && isSegmentLowConfidence(s.classifyConfidence))
      : segments;

  if (step === BatchIntakeStep.UPLOAD) {
    return (
      <Box sx={drawerContentStyles}>
        <MultiFileDropzone
          accept={ACCEPTED_FILE_TYPES}
          browseButtonTestId={AiBatchIntakeTestIds.BROWSE_FILES_BUTTON}
          hint={
            <Typography color="text.secondary" variant="regular14">
              Drop PDFs, images, or a ZIP — AI splits & sorts them · up to {SPLIT_MAX_FILE_SIZE_MB}{' '}
              MB per file
            </Typography>
          }
          onFilesSelected={processFiles}
        >
          <Box sx={modeTilesRowStyles} onClick={onStopPropagation}>
            {INTAKE_MODE_TILES.map(({ id, headline, description, Icon: TileIcon }) => (
              <Box key={id} sx={modeTileStyles}>
                <Box sx={modeTileIconStyles}>
                  <TileIcon size={16} />
                </Box>
                <Box sx={modeTileContentStyles}>
                  <Typography variant="bold12">{headline}</Typography>
                  <Typography color="text.secondary" variant="regular12">
                    {description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          {selectedFileNames.length > 0 && (
            <Box sx={modeTilesRowStyles} onClick={onStopPropagation}>
              {selectedFileNames.map(name => (
                <Typography color="text.secondary" key={name} variant="regular12">
                  {name}
                </Typography>
              ))}
            </Box>
          )}
        </MultiFileDropzone>
      </Box>
    );
  }

  if (isBackgroundReview && job?.status === DocumentIntakeJobStatus.COMPLETED && !job?.resultJson) {
    return (
      <Box sx={drawerContentStyles}>
        <Typography color="error.main" variant="regular14">
          Unable to load intake results — the analysis data could not be read. Please try again.
        </Typography>
      </Box>
    );
  }

  const segPlural = segments.length === 1 ? '' : 's';
  const subtitleText = deriveSubtitleText({
    isBackgroundReview,
    isSinglePdf,
    segments,
    segPlural,
    uploadLabel: job?.uploadLabel ?? null,
  });

  return (
    <Box sx={drawerContentStyles}>
      <Box sx={reviewHeaderStyles}>
        <Box sx={reviewHeaderAiBadgeStyles}>
          <AiSparkleIcon size={20} />
        </Box>
        <Box sx={reviewHeaderInfoStyles}>
          <Typography variant="bold16">
            {segments.length} document{segments.length === 1 ? '' : 's'} detected
          </Typography>
          {completionText && (
            <Typography
              data-testid={AiBatchIntakeTestIds.COMPLETION_BANNER}
              sx={reviewCompletionBannerStyles}
              variant="medium12"
            >
              {completionText}
            </Typography>
          )}
          <Typography color="text.secondary" variant="regular12">
            {subtitleText}
            {needsReviewCount > 0 && (
              <>
                {' · '}
                <Typography component="span" sx={reviewNeedsReviewInlineStyles} variant="bold12">
                  {needsReviewCount} need review
                </Typography>
              </>
            )}
          </Typography>
        </Box>
        <Box sx={reviewHeaderActionsStyles} />
      </Box>

      <Box sx={reviewToolbarStyles}>
        <Typography color="text.secondary" variant="regular12">
          {selectedCount} selected
        </Typography>
        <Box sx={reviewFilterSegmentStyles}>
          <Box
            sx={getReviewFilterSegmentItemStyles(reviewFilter === ReviewFilter.ALL)}
            onClick={onFilterChange(ReviewFilter.ALL)}
          >
            <Typography variant="regular12">All {segments.length}</Typography>
          </Box>
          <Box
            sx={getReviewFilterSegmentItemStyles(reviewFilter === ReviewFilter.NEEDS_REVIEW)}
            onClick={onFilterChange(ReviewFilter.NEEDS_REVIEW)}
          >
            <Typography variant="regular12">Needs review {needsReviewCount}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={reviewScrollAreaStyles}>
        {displayedSegments.map((seg, index) => {
          const showDivider = index > 0 && (isSinglePdf || isBackgroundReview);
          const prevNonRemovedIdx = displayedSegments
            .slice(0, index)
            .findLastIndex(s => !s.isRemoved);
          const hasPrev = prevNonRemovedIdx >= 0;
          const nextNonRemovedSeg = displayedSegments.slice(index + 1).find(s => !s.isRemoved);
          const hasNext = nextNonRemovedSeg != null;

          return (
            <React.Fragment key={seg.id}>
              {showDivider && (
                <Box sx={splitDividerStyles}>
                  <Box sx={splitDividerChipStyles}>
                    <Typography variant="regular12">AI split here</Typography>
                  </Box>
                  <Box sx={splitDividerLineStyles} />
                  {!isBackgroundReview && (
                    <Box sx={splitDividerChipStyles} onClick={onMergeUp(seg.id)}>
                      <Typography variant="regular12">⤺ Merge up</Typography>
                    </Box>
                  )}
                </Box>
              )}

              <IntakeSegmentRow
                documentTypeOptions={documentTypeOptions}
                hasNext={hasNext}
                hasPrev={hasPrev}
                isBackgroundReview={isBackgroundReview}
                previewOpenSegId={previewOpenSegId}
                reclassifyingSegIds={reclassifyingSegIds}
                seg={seg}
                splitPickerPage={splitPickerPage}
                splitPickerSegId={splitPickerSegId}
                onApplySplit={onApplySplit}
                onCancelSplit={onCancelSplit}
                onLoadSource={onLoadSource}
                onMergeWithNext={onMergeWithNext(seg.id)}
                onMergeWithPrev={onMergeWithPrev(seg.id)}
                onOpenSplitPicker={onOpenSplitPicker(seg.id)}
                onReclassify={onReclassify(seg)}
                onRemoveSegment={onRemoveSegment(seg.id)}
                onRestoreSegment={onRestoreSegment(seg.id)}
                onSegmentTypeChange={onSegmentTypeChange(seg.id)}
                onSplitPageSelect={onSplitPageSelect}
                onTogglePreview={onTogglePreview(seg.id)}
                onToggleSegment={onToggleSegment}
              />
            </React.Fragment>
          );
        })}
      </Box>

      <Box sx={reviewFooterStyles}>
        <Box sx={reviewFooterTallyStyles}>
          <Typography color="text.secondary" variant="regular12">
            <Typography component="span" variant="bold12">
              {filableCount}
            </Typography>
            {` of ${segments.length} will be filed`}
          </Typography>
          {removedCount > 0 && (
            <Typography color="text.secondary" variant="regular12">
              {'· '}
              <Typography component="span" variant="bold12">
                {removedCount}
              </Typography>
              {' removed'}
            </Typography>
          )}
          {editedCount > 0 && (
            <Typography color="text.secondary" variant="regular12">
              {'· '}
              <Typography component="span" variant="bold12">
                {editedCount}
              </Typography>
              {' edited'}
            </Typography>
          )}
          {readyCount > 0 && unassignedCount === 0 && needsReviewCount === 0 && (
            <Typography color="text.secondary" variant="regular12">
              {'· '}
              <Typography component="span" variant="bold12">
                {readyCount}
              </Typography>
              {' ready'}
            </Typography>
          )}
          {needsReviewCount > 0 && (
            <Typography color="text.secondary" variant="regular12">
              {'· '}
              <Typography component="span" sx={reviewNeedsReviewInlineStyles} variant="bold12">
                {needsReviewCount} need review
              </Typography>
            </Typography>
          )}
          {unassignedCount > 0 && (
            <Typography color="text.secondary" variant="regular12">
              {'· '}
              <Typography component="span" variant="bold12">
                {unassignedCount}
              </Typography>
              {' unassigned'}
            </Typography>
          )}
        </Box>
        <Box sx={reviewFooterActionsStyles}>
          {!isBackgroundReview && (
            <Button
              data-testid={AiBatchIntakeTestIds.CANCEL_BUTTON}
              disabled={isFiling}
              variant="outlined"
              onClick={onCancelToUpload}
            >
              Cancel
            </Button>
          )}
          <Button
            data-testid={AiBatchIntakeTestIds.CONFIRM_AND_FILE_BUTTON}
            disabled={isFiling || filableCount === 0}
            sx={reviewConfirmButtonStyles}
            variant="contained"
            onClick={onConfirmAndFile}
          >
            {isFiling ? (
              <>
                <CircularProgress color="inherit" size={16} style={confirmButtonIconStyle} />
                {`Filing ${filableCount} document${filableCount === 1 ? '' : 's'}…`}
              </>
            ) : (
              <>
                <AiSparkleIcon size={16} style={confirmButtonIconStyle} />
                {`Confirm & file ${filableCount} document${filableCount === 1 ? '' : 's'}`}
              </>
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

AiBatchIntake.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    uploadLabel: PropTypes.string,
    failedSegments: PropTypes.number,
    resultJson: PropTypes.shape({
      segments: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  onComplete: PropTypes.func,
};

export default memo(AiBatchIntake);
